"use client";

import * as React from "react";
import { ComponentProps } from "react";
import { cn } from "@repo/utils/cn";
import { Dropdown, DropdownTrigger, DropdownOption } from "./Dropdown";

export type SelectOption = {
  label: string;
  value: string;
};

export type SelectProps = {
  id?: string;
  placeholder?: string;
  value?: string;
  state?: "default" | "error";
  background?: "default" | "white";
  className?: string;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onValueChange?: (value: string) => void;
  children?: React.ReactNode;
} & ComponentProps<"div">;

export function Select({
  id,
  placeholder = "Placeholder",
  value = "",
  state = "default",
  background = "default",
  className,
  onChange,
  onValueChange,
  children,
  ...props
}: SelectProps) {
  const [isOpen, setIsOpen] = React.useState(false);

  const options: DropdownOption[] = React.useMemo(() => {
    const optionElements: DropdownOption[] = [];

    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child) && child.type === "option") {
        const optionProps =
          child.props as React.OptionHTMLAttributes<HTMLOptionElement>;
        if (optionProps.value && optionProps.value !== "") {
          optionElements.push({
            label: optionProps.children as string,
            value: String(optionProps.value),
          });
        }
      }
    });

    return optionElements;
  }, [children]);

  const selectedOption = options.find((opt) => opt.value === value);

  const handleValueChange = (newValue: string) => {
    onValueChange?.(newValue);

    if (onChange) {
      const syntheticEvent = {
        target: { value: newValue },
        currentTarget: { value: newValue },
      } as React.ChangeEvent<HTMLSelectElement>;
      onChange(syntheticEvent);
    }
  };

  const trigger = (
    <DropdownTrigger
      state={state}
      placeholder={placeholder}
      selectedLabel={selectedOption?.label}
      isOpen={isOpen}
      background={background}
    />
  );

  return (
    <div className={cn("uipm:w-full", className)} {...props}>
      <Dropdown
        trigger={trigger}
        options={options}
        value={value}
        onValueChange={handleValueChange}
        placeholder={placeholder}
        noneOptionLabel={placeholder}
        state={state}
        open={isOpen}
        onOpenChange={setIsOpen}
      />

      <select
        id={id}
        value={value}
        onChange={onChange}
        className="uipm:sr-only"
        tabIndex={-1}
        aria-hidden="true"
      >
        <option value="">{placeholder}</option>
        {children}
      </select>
    </div>
  );
}
